import React, { useState, useMemo } from 'react';

interface IdentityTeam {
  name: string;
  code?: number | string | null;
}

interface IdentityCandidate {
  filename: string;
  teams: IdentityTeam[];
}

interface Props {
  open: boolean;
  candidates: IdentityCandidate[] | null;
  trustedTeamName?: string | null;
  trustedTeamCode?: number | string | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onMap: (mapping: Record<string, { team_name: string; team_code: number }>) => void;
}

const TeamIdentityMappingModal: React.FC<Props> = ({
  open,
  candidates,
  trustedTeamName,
  trustedTeamCode,
  isSubmitting = false,
  onClose,
  onMap,
}) => {
  const [selected, setSelected] = useState<Record<string, IdentityTeam>>({});

  const resolvedCount = useMemo(
    () => (candidates || []).filter((c) => selected[c.filename]).length,
    [candidates, selected]
  );

  if (!open || !candidates) return null;

  const total = candidates.length;
  const allResolved = resolvedCount === total;

  const handleSelect = (filename: string, team: IdentityTeam) => {
    setSelected((prev) => ({ ...prev, [filename]: team }));
  };

  const handleSubmit = () => {
    if (!allResolved) return;
    const mapping: Record<string, { team_name: string; team_code: number }> = {};
    const fallbackCode = trustedTeamCode != null ? Number(trustedTeamCode) : 0;
    candidates.forEach((candidate) => {
      const team = selected[candidate.filename];
      mapping[candidate.filename] = {
        team_name: team.name,
        team_code: team.code != null ? Number(team.code) : fallbackCode,
      };
    });
    onMap(mapping);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Identify Your Team</h2>
        <p className="mb-4 text-sm text-gray-600">
          We could not automatically identify <strong>{trustedTeamName || 'your team'}</strong> in
          some game XMLs. Please select the correct team for each file.
        </p>

        <div className="max-h-96 overflow-y-auto rounded border border-gray-200">
          {candidates.map((candidate) => (
            <div key={candidate.filename} className="border-b border-gray-100 p-3 last:border-b-0">
              <p className="mb-2 text-xs font-medium text-gray-500">{candidate.filename}</p>
              <div className="flex flex-wrap gap-2">
                {candidate.teams.map((team) => {
                  const isSelected = selected[candidate.filename]?.name === team.name;
                  return (
                    <button
                      key={team.name}
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => handleSelect(candidate.filename, team)}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition ${
                        isSelected
                          ? 'border-sky-500 bg-sky-50 text-sky-900'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium">{team.name}</span>
                      {team.code != null && (
                        <span className="ml-2 text-xs text-gray-500">(code {team.code})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Resolved {resolvedCount} of {total}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !allResolved}
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving…' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamIdentityMappingModal;
